/**
 * OAuth-client data-access layer.
 *
 * Single owner of every SQL statement that reads or writes the Better
 * Auth-owned `oauthClient` table from application code (OPL-4285). Route
 * handlers parse the request, call one of these functions, and shape the
 * response — they never touch `@/lib/db`.
 *
 * The read statements below are moved verbatim from the routes: same columns,
 * same ORDER BY, same parameter order. The two hand-written INSERTs
 * (`app/api/oauth-clients` and `app/api/admin/oauth-clients`) are reconciled
 * into the single {@link createOAuthClient} below — see its doc comment for the
 * column union and the defaults.
 *
 * Not covered here (deliberately):
 * - the Better Auth `@better-auth/oauth-provider` plugin, which registers and
 *   mutates clients through its own adapter (`allowDynamicClientRegistration`)
 * - `app/api/oauth-clients/[clientId]/analytics`, whose statements land on
 *   another branch
 * - multi-table reporting joins (`authorized-apps`, `device/request`,
 *   certificates, agent capabilities) that only reference `oauthClient` as a
 *   join arm
 * - the `profile` reads the OAuth-client routes need for ownership checks,
 *   which lived here until OPL-4309 and now belong to
 *   `lib/data/profile-repository.ts`
 *
 * No HTTP, session, or `app/` concerns belong in this module.
 */
import { query } from "@/lib/db";

/**
 * Column list shared by the owner-facing reads and by the INSERT's RETURNING.
 */
const FULL_CLIENT_COLUMNS = `id, "clientId", name, "redirectUris", "accountPubkey", "ownerBapId", icon, uri, "logoUri", "tosUri", "policyUri", contacts, "themeOrigin", "createdAt", "updatedAt"`;

/** Column list used by the public `GET /api/oauth-clients/[clientId]` read. */
const PUBLIC_CLIENT_COLUMNS = `id, "clientId", name, "redirectUris", icon, uri, "logoUri", "tosUri", "policyUri", contacts, "themeOrigin", "createdAt", "updatedAt", "ownerBapId"`;

/** Row shape produced by {@link FULL_CLIENT_COLUMNS}. */
export interface OAuthClientRow {
	id: string;
	clientId: string;
	name: string;
	redirectUris: string[];
	accountPubkey: string | null;
	ownerBapId: string;
	icon: string | null;
	uri: string | null;
	logoUri: string | null;
	tosUri: string | null;
	policyUri: string | null;
	contacts: string | null;
	themeOrigin: string | null;
	createdAt: Date;
	updatedAt: Date;
}

/** Row shape produced by {@link PUBLIC_CLIENT_COLUMNS}. */
export interface PublicOAuthClientRow {
	id: string;
	clientId: string;
	name: string;
	redirectUris: string[];
	icon: string | null;
	uri: string | null;
	logoUri: string | null;
	tosUri: string | null;
	policyUri: string | null;
	contacts: string | null;
	themeOrigin: string | null;
	createdAt: string;
	updatedAt: string;
	ownerBapId: string;
}

/** Row shape returned to the admin listing. */
export interface AdminOAuthClientRow {
	id: string;
	clientId: string;
	name: string;
	redirectUris: string[];
	createdAt: Date;
	updatedAt: Date;
	ownerBapId: string;
	/** Signing key, surfaced so owners can wire integrations without re-registering. */
	accountPubkey: string | null;
}

/** Minimal row used for anonymous redirect-URI origin validation. */
export interface OAuthClientRedirectUrisRow {
	id: string;
	redirectUris: string[];
}

/** Identity pair returned by the legacy-owner recovery scan. */
export interface OAuthClientRecoveryRow {
	id: string;
	clientId: string;
}

/**
 * Fields accepted by {@link createOAuthClient}.
 *
 * `id` and `clientId` are supplied by the caller: the owner-facing route uses a
 * generated record id plus the caller's chosen `clientId`, while the admin
 * route generates one identifier and uses it for both.
 */
export interface CreateOAuthClientInput {
	id: string;
	clientId: string;
	name: string;
	redirectUris: string[];
	scopes: string[];
	ownerBapId: string;
	accountPubkey: string;
	icon: string | null;
	uri: string | null;
	logoUri: string | null;
	tosUri: string | null;
	policyUri: string | null;
	contacts: string | null;
	themeOrigin: string | null;
	isPublic: boolean;
}

/** Fields accepted by {@link updateOAuthClientById}. */
export interface UpdateOAuthClientInput {
	name: string;
	redirectUris: string[];
	icon: string | null;
	uri: string | null;
	logoUri: string | null;
	tosUri: string | null;
	policyUri: string | null;
	contacts: string | null;
	themeOrigin: string | null;
}

/**
 * Create an OAuth client.
 *
 * Unifies the two divergent hand-written INSERTs. The column set is the union
 * of both, and the values are the ones the Better Auth oauth-provider plugin
 * writes for a dynamically registered client:
 *
 * - `clientSecret` NULL (the plugin omits the secret for a public client)
 * - `disabled` false (the plugin's schema default)
 * - `userId` NULL (this server owns clients by `ownerBapId`, not by user row)
 * - `public` from the caller (both routes pass `true`; the plugin derives it
 *   from `token_endpoint_auth_method === "none"`)
 *
 * The admin INSERT previously left `themeOrigin`, `clientSecret`, `disabled`
 * and `userId` out of the statement entirely and inherited the column
 * defaults; writing them explicitly produces the same stored row
 * (`disabled DEFAULT false`, the others nullable with no default).
 */
export async function createOAuthClient(
	input: CreateOAuthClientInput
): Promise<OAuthClientRow | null> {
	const result = await query<OAuthClientRow>(
		`INSERT INTO "oauthClient" (id, "clientId", name, "redirectUris", scopes, "ownerBapId", "accountPubkey", icon, uri, "logoUri", "tosUri", "policyUri", contacts, "themeOrigin", "clientSecret", "public", disabled, "userId", "createdAt", "updatedAt", "tokenEndpointAuthMethod")
		 VALUES ($1, $2, $3, $4::jsonb, $5::jsonb, $6, $7, $8, $9, $10, $11, $12, $13, $14, NULL, $15, false, NULL, NOW(), NOW(), CASE WHEN $15 THEN 'none' ELSE 'client_secret_basic' END)
		 RETURNING ${FULL_CLIENT_COLUMNS}`,
		[
			input.id,
			input.clientId,
			input.name,
			JSON.stringify(input.redirectUris),
			JSON.stringify(input.scopes),
			input.ownerBapId,
			input.accountPubkey,
			input.icon,
			input.uri,
			input.logoUri,
			input.tosUri,
			input.policyUri,
			input.contacts,
			input.themeOrigin,
			input.isPublic,
		]
	);
	return result.rows[0] ?? null;
}

/** Every client owned by one BAP id, newest first. */
export async function listOAuthClientsForOwner(
	bapId: string
): Promise<OAuthClientRow[]> {
	const result = await query<OAuthClientRow>(
		`SELECT ${FULL_CLIENT_COLUMNS}
		 FROM "oauthClient"
		 WHERE "ownerBapId" = $1
		 ORDER BY "createdAt" DESC`,
		[bapId]
	);
	return result.rows;
}

/** Every client owned by any of the supplied owner ids, newest first. */
export async function listOAuthClientsForOwners(
	ownerIds: string[]
): Promise<OAuthClientRow[]> {
	const placeholders = ownerIds.map((_, i) => `$${i + 1}`).join(", ");
	const result = await query<OAuthClientRow>(
		`SELECT ${FULL_CLIENT_COLUMNS}
		 FROM "oauthClient"
		 WHERE "ownerBapId" IN (${placeholders})
		 ORDER BY "createdAt" DESC`,
		ownerIds
	);
	return result.rows;
}

/** One client by its OAuth `clientId`, or null when it does not exist. */
export async function findOAuthClientByClientId(
	clientId: string
): Promise<OAuthClientRow | null> {
	const result = await query<OAuthClientRow>(
		`SELECT ${FULL_CLIENT_COLUMNS}
		 FROM "oauthClient"
		 WHERE "clientId" = $1
		 LIMIT 1`,
		[clientId]
	);
	return result.rows[0] ?? null;
}

/** Public projection of one client, used by the unauthenticated GET. */
export async function findPublicOAuthClientByClientId(
	clientId: string
): Promise<PublicOAuthClientRow | null> {
	const result = await query<PublicOAuthClientRow>(
		`SELECT ${PUBLIC_CLIENT_COLUMNS}
       FROM "oauthClient"
       WHERE "clientId" = $1`,
		[clientId]
	);
	return result.rows[0] ?? null;
}

/** Owner of a client, used for the friendly duplicate-clientId message. */
export async function findOAuthClientOwnerByClientId(
	clientId: string
): Promise<string | null> {
	const result = await query<{ ownerBapId: string }>(
		'SELECT "ownerBapId" FROM "oauthClient" WHERE "clientId" = $1',
		[clientId]
	);
	return result.rows[0]?.ownerBapId ?? null;
}

/**
 * `id` + `redirectUris` for every registered client. Anonymous callers use this
 * to validate an origin without seeing client metadata.
 */
export async function listOAuthClientRedirectUris(): Promise<
	OAuthClientRedirectUrisRow[]
> {
	const result = await query<OAuthClientRedirectUrisRow>(
		'SELECT id, "redirectUris" FROM "oauthClient"'
	);
	return result.rows;
}

/** Redirect-URI arrays only; feeds the credentialed-CORS origin allowlist. */
export async function listOAuthClientRedirectUriSets(): Promise<
	Array<{ redirectUris: string[] | null }>
> {
	const result = await query<{ redirectUris: string[] | null }>(
		'SELECT "redirectUris" FROM "oauthClient"'
	);
	return result.rows;
}

/** Whether the client row `id` is owned by any of `ownerIds`. */
export async function isOAuthClientOwnedBy(
	id: string,
	ownerIds: string[]
): Promise<boolean> {
	const placeholders = ownerIds.map((_, i) => `$${i + 1}`).join(", ");
	const result = await query(
		`SELECT id FROM "oauthClient"
     WHERE id = $${ownerIds.length + 1}
     AND "ownerBapId" IN (${placeholders})`,
		[...ownerIds, id]
	);
	return result.rows.length > 0;
}

/** Owner-facing update by record id; returns the updated row or null. */
export async function updateOAuthClientById(
	id: string,
	fields: UpdateOAuthClientInput
): Promise<PublicOAuthClientRow | null> {
	const result = await query<PublicOAuthClientRow>(
		`UPDATE "oauthClient"
       SET name = $1, "redirectUris" = $2::jsonb, icon = $3, uri = $4, "logoUri" = $5, "tosUri" = $6, "policyUri" = $7, contacts = $8, "themeOrigin" = $9, "updatedAt" = NOW()
       WHERE id = $10
       RETURNING ${PUBLIC_CLIENT_COLUMNS}`,
		[
			fields.name,
			JSON.stringify(fields.redirectUris),
			fields.icon,
			fields.uri,
			fields.logoUri,
			fields.tosUri,
			fields.policyUri,
			fields.contacts,
			fields.themeOrigin,
			id,
		]
	);
	return result.rows[0] ?? null;
}

/** Owner-facing delete by record id; returns the former owner or null. */
export async function deleteOAuthClientById(
	id: string
): Promise<string | null> {
	const result = await query<{ ownerBapId: string }>(
		'DELETE FROM "oauthClient" WHERE id = $1 RETURNING "ownerBapId"',
		[id]
	);
	return result.rows[0]?.ownerBapId ?? null;
}

/** Every client, newest first, for the admin listing. */
export async function listAllOAuthClients(): Promise<AdminOAuthClientRow[]> {
	const result = await query<AdminOAuthClientRow>(
		'SELECT id, "clientId", name, "redirectUris", "createdAt", "updatedAt", "ownerBapId", "accountPubkey" FROM "oauthClient" ORDER BY "createdAt" DESC'
	);
	return result.rows;
}

/**
 * Admin update by OAuth `clientId`. Returns `null` when nothing matched;
 * otherwise the same admin-safe column set as `listAllOAuthClients`, which the
 * admin route echoes back. The column list is explicit on purpose: returning
 * every column echoed `clientSecret` to the caller on every edit (OPL-4307).
 */
export async function updateOAuthClientByClientId(
	clientId: string,
	name: string,
	redirectUris: string[]
): Promise<AdminOAuthClientRow | null> {
	const result = await query<AdminOAuthClientRow>(
		'UPDATE "oauthClient" SET name = $1, "redirectUris" = $2::jsonb, "updatedAt" = NOW() WHERE "clientId" = $3 RETURNING id, "clientId", name, "redirectUris", "createdAt", "updatedAt", "ownerBapId"',
		[name, JSON.stringify(redirectUris), clientId]
	);
	if (result.rowCount === 0) {
		return null;
	}
	return result.rows[0] ?? null;
}

/**
 * Admin delete by OAuth `clientId`. Returns `null` when nothing matched, and
 * `{ ownerBapId }` (possibly undefined) when a row was removed — the caller
 * distinguishes "not found" from "deleted".
 */
export async function deleteOAuthClientByClientId(
	clientId: string
): Promise<{ ownerBapId?: string } | null> {
	const result = await query<{ ownerBapId: string }>(
		'DELETE FROM "oauthClient" WHERE "clientId" = $1 RETURNING "ownerBapId"',
		[clientId]
	);
	if (result.rowCount === 0) {
		return null;
	}
	return { ownerBapId: result.rows[0]?.ownerBapId };
}

/** `themeOrigin` for one client, or null when unknown/unset. */
export async function findOAuthClientThemeOrigin(
	clientId: string
): Promise<string | null> {
	const result = await query<{ themeOrigin: string | null }>(
		`SELECT "themeOrigin" FROM "oauthClient" WHERE "clientId" = $1 LIMIT 1`,
		[clientId]
	);
	return result.rows[0]?.themeOrigin ?? null;
}

/** Clients registered to a legacy BAP id and signed for by `accountPubkey`. */
export async function listOAuthClientsForOwnerAndAccountPubkey(
	ownerBapId: string,
	accountPubkey: string
): Promise<OAuthClientRecoveryRow[]> {
	const result = await query<OAuthClientRecoveryRow>(
		`SELECT id, "clientId"
		   FROM "oauthClient"
		  WHERE "ownerBapId" = $1
		    AND "accountPubkey" = $2`,
		[ownerBapId, accountPubkey]
	);
	return result.rows;
}

/**
 * Every `clientId` signed for by `accountPubkey`, across owners. Claim reads
 * authorize by key ownership: a caller proving this key may read claims
 * bound to any of these clients. One key may own many clients, so callers
 * get the list, not a single row.
 */
export async function listOAuthClientIdsForAccountPubkey(
	accountPubkey: string
): Promise<string[]> {
	const result = await query<{ clientId: string }>(
		`SELECT "clientId"
		   FROM "oauthClient"
		  WHERE "accountPubkey" = $1`,
		[accountPubkey]
	);
	return result.rows.map((row) => row.clientId);
}

/** Move the listed client rows to a new owner BAP id. */
export async function reassignOAuthClientOwner(
	newBapId: string,
	ids: string[]
): Promise<void> {
	await query(
		`UPDATE "oauthClient"
		    SET "ownerBapId" = $1, "updatedAt" = NOW()
		  WHERE id = ANY($2::text[])`,
		[newBapId, ids]
	);
}
